// Public API: suggestColumnsInScope(scope, snapshot, filter?) → Column[].
// Pure function. Walks the scope's reachable tables (plus the parent chain),
// joins each to the SchemaSnapshot's columns through `lookupRelationColumns`
// (schema-aware: `mia_s1.orders o` gets mia_s1's columns, `FROM orders` the
// default schema's), applies an
// optional case-insensitive prefix filter, and returns a deterministically
// ordered (stable sort by relation folded name, then by source column ordinal),
// de-duplicated list.
//
// Anti-SOUNDEX invariant: when the caller has already narrowed scope to a
// single relation (e.g. dotted-access `alias.|`), it passes a Scope whose
// `reachableTables` contains ONLY that relation. This function returns
// ONLY that relation's columns — never merges siblings in.
//
// Deduplication: when the same column is visible through multiple scopes
// (e.g. correlated subquery + outer statement), the nearest-scope binding
// wins for the Column.relation field. Implementation: walk from the innermost
// scope outward, keep the first (relationKey(relation), column.foldedName)
// hit — keyed by the RESOLVED relation, so an alias and the direct entry of
// the same table dedupe while two schemas' same-named tables do not.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Suggestion — Columns
// in Scope.

import type { Column, SchemaSnapshot, Scope, ReachableTable } from '../types';
import { identifierKey, lookupRelationColumns, reachableRelation, relationKey } from '../service/snapshotRelations';

export function suggestColumnsInScope(
  scope: Scope,
  snapshot: SchemaSnapshot | undefined,
  filter?: string,
): Column[] {
  if (!snapshot) return [];

  const out: Column[] = [];
  const seen = new Set<string>();
  const filterLower = filter ? filter.toLowerCase() : undefined;

  for (const rt of walkScopeTables(scope)) {
    const cols = lookupRelationColumns(snapshot, reachableRelation(rt));
    if (!cols || cols.length === 0) continue;
    for (const col of cols) {
      const key = `${relationKey(col.relation)}.${identifierKey(col.qualified.foldedName)}`;
      if (seen.has(key)) continue;
      if (filterLower && !col.qualified.name.toLowerCase().startsWith(filterLower)) {
        continue;
      }
      seen.add(key);
      // Preserve the snapshot column as-is (its .relation already points at the
      // backing table's QualifiedName). Nearest-scope binding is achieved by
      // the walk order — the first hit per (relation, column) wins.
      out.push(col);
    }
  }

  // Stable sort by relation folded name. Because we collected columns grouped
  // by relation (outer loop = reachable table) and Array.sort is stable in
  // modern JS, within-relation source ordinal is preserved.
  out.sort((a, b) => {
    const ar = a.relation.foldedName;
    const br = b.relation.foldedName;
    if (ar < br) return -1;
    if (ar > br) return 1;
    return 0;
  });

  return out;
}

// Walk the scope chain innermost-first, yielding every ReachableTable.
// Duplicate relations (same foldedName appearing at multiple scopes) are
// kept in this generator — the caller's per-(relation, column) dedup Set
// squashes them on output while preserving nearest-scope precedence.
function* walkScopeTables(scope: Scope): Generator<ReachableTable> {
  let s: Scope | undefined = scope;
  while (s) {
    for (const rt of s.reachableTables) yield rt;
    s = s.parent;
  }
}
